import { Link, Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Shell } from "../components/Shell";
import { Ornament } from "../components/Ornament";
import { WeekNavigator } from "../components/WeekNavigator";
import type { ShoppingListResponse } from "../lib/types";
import { thisMondayIso } from "../lib/week";

function isValidIsoDate(iso: string | undefined): iso is string {
  return typeof iso === "string" && /^\d{4}-\d{2}-\d{2}$/.test(iso);
}

/** Format a (possibly fractional) quantity for display. */
function formatQty(q: number): string {
  if (Number.isInteger(q)) return String(q);
  // Keep two decimal places, then strip trailing zeros.
  return q.toFixed(2).replace(/\.?0+$/, "");
}

export function ShoppingListPage() {
  const { week_start } = useParams<{ week_start?: string }>();

  if (week_start !== undefined && !isValidIsoDate(week_start)) {
    return <Navigate to="/shopping-list" replace />;
  }

  const weekStartIso = week_start ?? thisMondayIso();

  return <ShoppingListView weekStartIso={weekStartIso} />;
}

function ShoppingListView({ weekStartIso }: { weekStartIso: string }) {
  const { data, isLoading, isError } = useQuery<ShoppingListResponse>({
    queryKey: ["shopping-list", weekStartIso],
    queryFn: () =>
      api.get<ShoppingListResponse>(
        `/api/meal-plans/${weekStartIso}/shopping-list`,
      ),
  });

  const recipeItems = data?.from_recipes ?? [];
  const regulars = data?.from_regulars ?? [];
  const totalLines = recipeItems.length + regulars.length;

  return (
    <Shell>
      <section className="mb-16 grid grid-cols-12 gap-8 border-b border-ink/80 pb-14">
        <div
          className="col-span-12 md:col-span-3 animate-fade-up"
          style={{ animationDelay: "60ms" }}
        >
          <div className="label mb-3 text-ink-walnut">Section</div>
          <div className="label text-sienna">N° 05</div>
          <div className="label mt-1 text-ink-walnut">
            {totalLines} {totalLines === 1 ? "line" : "lines"}
          </div>
        </div>
        <div
          className="col-span-12 md:col-span-9 animate-fade-up"
          style={{ animationDelay: "140ms" }}
        >
          <h1 className="display-1">
            This week's <span className="display-italic">list</span>
          </h1>
          <p className="mt-8 max-w-xl font-body text-xl italic leading-snug text-ink-walnut">
            Everything the plan asks for — drawn from the recipes and from the
            pantry regulars, copied out long-hand before the trip to market.
          </p>
        </div>
      </section>

      <section
        className="mb-14 animate-fade-up"
        style={{ animationDelay: "220ms" }}
      >
        <WeekNavigator weekStartIso={weekStartIso} basePath="/shopping-list" />
      </section>

      {isLoading && (
        <p className="label animate-fade-in text-ink-muted">
          Transcribing the list…
        </p>
      )}

      {isError && (
        <div className="border-l-2 border-sienna pl-4 font-body italic text-sienna">
          The list could not be prepared. Try again in a moment.
        </div>
      )}

      {data && (
        <div className="grid grid-cols-12 gap-x-8 gap-y-16">
          <section
            className="col-span-12 md:col-span-7 animate-fade-up"
            style={{ animationDelay: "300ms" }}
          >
            <div className="mb-8 flex items-center gap-4">
              <div className="label text-sienna">N° 01</div>
              <div className="label text-ink-walnut">From the recipes</div>
              <span className="hairline-ink flex-1 origin-left animate-rule-sweep" />
            </div>

            {recipeItems.length === 0 && (
              <p className="font-body italic text-ink-muted">
                No recipes on the plan yet — nothing to draw from.
              </p>
            )}

            {recipeItems.length > 0 && (
              <ul>
                {recipeItems.map((item, idx) => (
                  <li
                    key={`${item.recipe_id}-${idx}`}
                    className="grid grid-cols-[auto_1fr_auto] items-baseline gap-x-5 border-b border-ink/10 py-4"
                  >
                    <span className="label text-ink-muted">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <div className="font-body text-lg leading-snug text-ink">
                        {item.raw_text}
                      </div>
                      <div className="label mt-1 text-ink-muted">
                        from {item.recipe_title}
                      </div>
                    </div>
                    {item.quantity !== null && (
                      <div className="label text-ink-walnut">
                        {formatQty(item.quantity)}
                        {item.unit ? ` ${item.unit}` : ""}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section
            className="col-span-12 md:col-span-5 animate-fade-up"
            style={{ animationDelay: "380ms" }}
          >
            <div className="mb-8 flex items-center gap-4">
              <div className="label text-sienna">N° 02</div>
              <div className="label text-ink-walnut">From the regulars</div>
              <span className="hairline-ink flex-1 origin-left animate-rule-sweep" />
            </div>

            {regulars.length === 0 && (
              <p className="font-body italic text-ink-muted">
                No active regulars. Add some on the{" "}
                <a href="/regulars" className="ink-link text-ink">
                  regulars page
                </a>
                .
              </p>
            )}

            {regulars.length > 0 && (
              <ul>
                {regulars.map((r, idx) => (
                  <li
                    key={r.id}
                    className="grid grid-cols-[auto_1fr_auto] items-baseline gap-x-5 border-b border-ink/10 py-4"
                  >
                    <span className="label text-ink-muted">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <div className="font-body text-lg leading-snug text-ink">
                        {r.name}
                      </div>
                      {r.notes && (
                        <div className="label mt-1 text-ink-muted">
                          {r.notes}
                        </div>
                      )}
                    </div>
                    <div className="label text-ink-walnut">
                      × {r.default_quantity}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}

      <div className="mt-24 border-t border-ink/20 pt-12">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="max-w-md">
            <div className="label mb-2 text-sienna">N° 03 — To follow</div>
            <p className="font-body text-base italic text-ink-walnut">
              Hand the list to the matcher — Claude will look up each
              ingredient at Morrisons and propose a product, ready for your
              review before it reaches the basket.
            </p>
          </div>
          <Link to={`/basket-plan/${weekStartIso}`} className="btn-ink shrink-0">
            Generate basket plan
          </Link>
        </div>
      </div>

      <div className="mt-24 flex items-center gap-6">
        <span className="hairline flex-1" />
        <Ornament className="h-4 w-20 text-sienna" />
        <span className="hairline flex-1" />
      </div>
    </Shell>
  );
}
